"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { AxiosError, AxiosResponse } from "axios"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import z from "zod"

import { useStoreDocuments } from "@/features/MyDrive/my-drive/store/my-drive"
import useModal from "@/shared/hooks/useModal"
import { authService } from "@/shared/lib/auth-service"
import { useStoreApp } from "@/shared/store/app"

import { login, LoginResponse } from "../api/login"

type LoginApiResponse = AxiosResponse<LoginResponse>

export const loginSchema = z.object({
  emailOrUsername: z
    .string()
    .min(1, { message: "Email is required" })
    .email({ message: "Invalid email" }),
  password: z.string().min(1, { message: "Password is required" }),
})

export const useLogin = () => {
  const { setSidebarCollapse } = useStoreApp()
  const { setDocuments } = useStoreDocuments()
  const { isOpen, openModal, closeModal } = useModal()

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { emailOrUsername: "", password: "" },
  })

  const { mutate, isPending: isLoadingLogin } = useMutation({
    mutationFn: login,
    onSuccess: (response: LoginApiResponse) => {
      setDocuments([])
      setSidebarCollapse(false)

      const data = response?.data

      // Extract token from response
      const token =
        data?.data?.token ||
        data?.data?.sso_token ||
        (data as any)?.token ||
        (data as any)?.sso_token

      if (!token) {
        toast.error("Login failed: token not received")
        return
      }

      authService.setTempToken(token)

      const message = data?.meta?.message ?? ""

      if (message === "Login successful" || data?.meta?.code === 200) {
        toast.success("Success Login", { description: message })
        const redirect = new URLSearchParams(window.location.search).get("redirect")
        window.location.href = redirect
          ? `/auth/select-company?redirect=${encodeURIComponent(redirect)}`
          : "/auth/select-company"
      } else if ((data?.meta?.code as any) === 422) {
        // Active session on another device
        openModal()
      } else {
        toast.error("Failed Login", { description: message })
      }
    },
    onError: (error: unknown) => {
      if (error instanceof AxiosError && error.response?.status === 401) {
        toast.error("Invalid email or password")
      } else {
        const errorMessage =
          (error instanceof AxiosError && error.response?.data?.meta?.message) ||
          (error instanceof AxiosError && error.response?.data?.message) ||
          "An unexpected error occurred"
        toast.error(errorMessage)
      }
    },
    mutationKey: ["login"],
  })

  const onSubmit = (data: z.infer<typeof loginSchema>) => {
    mutate(data)
  }

  return {
    form,
    isLoadingLogin,
    onSubmit,
    isOpen,
    closeModal,
  }
}
